import { useQuery } from "@apollo/client";
import { TabContext, TabPanel } from "@mui/lab";
import { Alert, Box, Stack, Tab, Tabs, Typography } from "@mui/material";
import ScoutIcon from "components/ScoutIcon";
import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";

import type { BenchmarksView } from "./types";

import { BenchmarksTab } from "./BenchmarksTab";
import { EvalsTab } from "./EvalsTab";
import { LoadingState, ScoutEvalExplainerButton } from "./ScoutEvalCommon";
import { SCOUT_EVAL_DASHBOARD_FOR_STAFF } from "./scoutEvalQuery";
import { buildBenchmarksUrl, pagePaddingSx } from "./scoutEvalUtils";

function SubTabs({ activeView }: { activeView: BenchmarksView }) {
  return (
    <Tabs
      value={activeView}
      aria-label="Benchmark dashboard tabs"
      sx={{ borderBottom: 1, borderColor: "divider" }}
    >
      <Tab
        value="evals"
        label="Evals"
        component={Link}
        to={buildBenchmarksUrl({ view: "evals" })}
      />
      <Tab
        value="benchmarks"
        label="Benchmarks"
        component={Link}
        to={buildBenchmarksUrl({ view: "benchmarks" })}
      />
    </Tabs>
  );
}

export function ScoutEvals() {
  const [searchParams] = useSearchParams();
  const activeView: BenchmarksView =
    searchParams.get("benchmarksView") === "benchmarks" ? "benchmarks" : "evals";
  const selectedEvalId = searchParams.get("evalResult");
  const { data, loading, error } = useQuery(SCOUT_EVAL_DASHBOARD_FOR_STAFF, {
    variables: { evalLimit: 100 },
  });

  const dashboard = data?.getScoutEvalDashboardForStaff;
  const benchmarks = useMemo(() => dashboard?.benchmarks ?? [], [dashboard?.benchmarks]);
  const evalRows = useMemo(() => dashboard?.evals ?? [], [dashboard?.evals]);

  return (
    <Stack spacing={3} sx={pagePaddingSx}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} minWidth={0}>
          <ScoutIcon sx={{ color: "encampBlue.main", width: 36, height: 36 }} />
          <Box minWidth={0}>
            <Typography variant="h4" fontWeight={600}>
              Scout Evals
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Benchmark results and rubric definitions for Scout answer quality.
            </Typography>
          </Box>
        </Stack>
        <ScoutEvalExplainerButton />
      </Stack>
      <SubTabs activeView={activeView} />
      <TabContext value={activeView}>
        <TabPanel value="evals" sx={{ p: 0 }}>
          {loading ? <LoadingState /> : null}
          {error ? (
            <Alert severity="error" variant="outlined">
              Failed to load Scout eval dashboard: {error.message}
            </Alert>
          ) : null}
          {!loading && !error ? (
            <EvalsTab evalRows={evalRows} benchmarks={benchmarks} selectedEvalId={selectedEvalId} />
          ) : null}
        </TabPanel>
        <TabPanel value="benchmarks" sx={{ p: 0 }}>
          {loading ? <LoadingState /> : null}
          {error ? (
            <Alert severity="error" variant="outlined">
              Failed to load Scout eval benchmarks: {error.message}
            </Alert>
          ) : null}
          {!loading && !error ? <BenchmarksTab benchmarks={benchmarks} /> : null}
        </TabPanel>
      </TabContext>
    </Stack>
  );
}
